import React from "react";
import { Mail, Instagram, Linkedin, ArrowRight, ExternalLink } from "lucide-react";
import { Sunflower, SunflowerMini } from "@/components/site/Sunflower";

const EMAIL = "zahabinada@gmail.com";
const INSTAGRAM_URL = "https://instagram.com/nadaskidsgarden";
const LINKEDIN_URL = "https://www.linkedin.com/in/nada-z-50b840154/";

export default function Contact() {
  return (
    <div data-testid="contact-page">
      {/* HERO */}
      <section className="section-pad relative overflow-hidden petal-bg">
        <div className="absolute -top-16 -right-16 opacity-35 pointer-events-none hidden md:block">
          <Sunflower size={240} className="animate-slowspin" />
        </div>
        <div className="relative max-w-3xl">
          <span className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase text-brand-leafDeep bg-brand-honey/50 border border-brand-sun/40 rounded-full px-4 py-1.5 mb-6 w-fit">
            <SunflowerMini size={18} />
            Get in touch
          </span>
          <h1 className="font-heading font-normal text-[2.75rem] sm:text-5xl lg:text-[4.25rem] leading-[1.02] tracking-tight">
            Let's <span className="sunray-underline">talk.</span>
          </h1>
          <p className="mt-7 text-brand-muted text-base md:text-lg leading-relaxed font-body">
            Whether you're a parent quietly Googling at midnight, a school
            looking to build something better for your students, a journalist
            on a deadline, or a brand wanting to collaborate — Nada reads
            every message herself. Reach out the way that suits you best.
          </p>
        </div>
      </section>

      {/* CONTACT CARDS */}
      <section className="section-pad pt-0">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Email */}
          <div className="bg-white rounded-[28px] p-8 border-2 border-brand-line hover:border-brand-sun/50 transition-colors h-full flex flex-col">
            <div className="h-12 w-12 rounded-2xl bg-brand-sun/25 text-brand-sunDeep grid place-items-center mb-6">
              <Mail size={22} />
            </div>
            <h3 className="font-heading text-xl mb-2">Email Nada</h3>
            <p className="text-sm text-brand-muted leading-relaxed font-body flex-1">
              For inquiries, media, partnerships, or any question — big or
              small. Replies usually within 1–2 business days.
            </p>
            <a
              data-testid="contact-email-link"
              href={`mailto:${EMAIL}`}
              className="mt-7 inline-flex items-center justify-center gap-2 rounded-full bg-brand-ink hover:bg-brand-leafDeep text-brand-cream px-6 py-3 text-sm font-medium transition-colors shadow-earthy w-fit"
            >
              {EMAIL} <ArrowRight size={14} />
            </a>
          </div>

          {/* LinkedIn */}
          <div className="bg-white rounded-[28px] p-8 border-2 border-brand-line hover:border-brand-sun/50 transition-colors h-full flex flex-col">
            <div className="h-12 w-12 rounded-2xl bg-brand-sky/40 text-brand-leafDeep grid place-items-center mb-6">
              <Linkedin size={22} />
            </div>
            <h3 className="font-heading text-xl mb-2">Follow on LinkedIn</h3>
            <p className="text-sm text-brand-muted leading-relaxed font-body flex-1">
              For professional updates, talks, and behind-the-scenes from
              schools and curriculum work.
            </p>
            <a
              data-testid="contact-linkedin-btn"
              href={LINKEDIN_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-7 inline-flex items-center justify-center gap-2 rounded-full bg-brand-ink hover:bg-brand-leafDeep text-brand-cream px-6 py-3 text-sm font-medium transition-colors shadow-earthy w-fit"
            >
              Follow <ExternalLink size={14} />
            </a>
          </div>

          {/* Instagram */}
          <div className="bg-white rounded-[28px] p-8 border-2 border-brand-line hover:border-brand-sun/50 transition-colors h-full flex flex-col">
            <div className="h-12 w-12 rounded-2xl bg-brand-terracotta/20 text-brand-rust grid place-items-center mb-6">
              <Instagram size={22} />
            </div>
            <h3 className="font-heading text-xl mb-2">Instagram</h3>
            <p className="text-sm text-brand-muted leading-relaxed font-body flex-1">
              Daily warmth, parenting tips, sneak peeks of new books, and a
              window into Nada's everyday work with kids.
            </p>
            <a
              data-testid="contact-instagram-link"
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-7 inline-flex items-center justify-center gap-2 rounded-full bg-brand-ink hover:bg-brand-leafDeep text-brand-cream px-6 py-3 text-sm font-medium transition-colors shadow-earthy w-fit"
            >
              @nadaskidsgarden <ExternalLink size={14} />
            </a>
          </div>
        </div>
      </section>

      {/* MESSAGE CTA */}
      <section className="section-pad pt-0">
        <div className="relative bg-gradient-to-br from-brand-sun/30 via-brand-honey/40 to-brand-leafSoft/25 rounded-[36px] border-2 border-brand-sun/40 overflow-hidden">
          <div className="absolute -top-10 -left-10 opacity-25 pointer-events-none">
            <Sunflower size={200} className="animate-slowspin" />
          </div>
          <div className="absolute -bottom-10 -right-10 opacity-25 pointer-events-none">
            <Sunflower size={200} showLeaves className="animate-slowspin" />
          </div>

          <div className="relative grid md:grid-cols-12 gap-8 p-10 md:p-14 items-center">
            <div className="md:col-span-7">
              <span className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase text-brand-leafDeep bg-white border border-brand-sun/40 rounded-full px-4 py-1.5 mb-5 w-fit">
                <Mail size={14} /> Direct line
              </span>
              <h2 className="font-heading font-normal text-3xl md:text-4xl lg:text-5xl leading-[1.05]">
                The fastest way to reach Nada.
              </h2>
              <p className="mt-5 text-brand-muted leading-relaxed font-body max-w-xl">
                Send a short email — your child's age, what's hard right now,
                and what you're hoping for. Nada reads every message herself
                and replies within 1–2 business days.
              </p>
            </div>

            <div className="md:col-span-5">
              <div className="bg-white rounded-[28px] p-7 border-2 border-brand-line shadow-earthy">
                <div className="h-12 w-12 rounded-2xl bg-brand-sun/25 text-brand-sunDeep grid place-items-center mb-5">
                  <Mail size={22} />
                </div>
                <h3 className="font-heading text-2xl mb-1">Email Nada</h3>
                <p className="text-sm text-brand-muted font-body leading-relaxed break-all">
                  {EMAIL}
                </p>
                <a
                  data-testid="contact-email-cta"
                  href={`mailto:${EMAIL}`}
                  className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-brand-ink hover:bg-brand-leafDeep text-brand-cream px-6 py-3.5 text-sm font-semibold transition-colors shadow-earthy w-full"
                >
                  Send a message <ArrowRight size={14} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
